// Tecla que fecha a conversa aberta (fidelidade ao WhatsApp Web).
export const ESC_KEY = 'Escape'

// Tags de campo de texto onde o ESC NÃO deve fechar a conversa (o usuário
// está digitando — ESC ali serve para limpar/sair do próprio campo).
export const EDITABLE_TAGS = ['INPUT', 'TEXTAREA']

// Classe do overlay ativo de um modal Vuetify. Se existir um modal aberto na
// frente, o ESC pertence ao modal — não fechamos a conversa por baixo dele.
export const ACTIVE_OVERLAY_SELECTOR = '.v-overlay--active'

// Intervalo de polling da lista de conversas na view principal (ms).
export const POLL_INTERVAL_MS = 5000

// Máximo de erros consecutivos no polling antes de parar automaticamente.
export const MAX_CONSECUTIVE_ERRORS = 3

// Tamanho de página padrão ao listar mensagens de uma conversa. Alinhado
// em 100 entre o open (onSelectConversa → listarMensagens) e o poll
// (fetchSelectedMensagens) — o backend usa o mesmo valor; divergência
// fazia o open carregar menos mensagens que o poll e disparar dedup/sync
// inconsistente.
export const MENSAGENS_PER_PAGE = 100

// Heurística barata de "conversa defasada" no open (ADR-2): se a conversa
// tem menos mensagens locais que este piso, vale a pena tentar uma
// sincronização incremental em background. Conversas reais costumam ter
// histórico maior; poucas mensagens locais indicam DB ainda não populado.
export const STALE_LOCAL_MENSAGENS_THRESHOLD = 3

// Quantidade pedida ao importar histórico no sync-on-open (best-effort).
export const STALE_SYNC_IMPORT_QUANTIDADE = 100

// Tokens de cor dos painéis da view-atendimento/popup (bg, hover, scroll).
// Usado para expor CSS vars `--panel-*` via :style binding.
export const PANEL_STYLES = {
  flatGray: { bg: '#F0F1F3', bgHover: '#e4e5e7', scrollTrack: '#F0F1F3' },
  flatWhite: { bg: '#FFFFFF', bgHover: '#f5f5f5', scrollTrack: '#FFFFFF' },
}

/**
 * As CSS vars `--panel-*` de um estilo de painel, para o `:style` do elemento
 * que embrulha os componentes da caixa de atendimento.
 *
 * ★ Quem monta `conversation-list-item` FORA de uma das quatro views de painel
 * (view-atendimento, omni-atendimento, atendimento-popup, view-comunicacao)
 * precisa declarar estes tokens no próprio embrulho — é o caso dos cartões do
 * chat, que montam a linha de conversa dentro da bolha. Sem provedor a `var()`
 * não resolve, e o CSS descarta a DECLARAÇÃO INTEIRA: a divisória volta a
 * `border-style: none` e o fundo a `transparent`, deixando a lista clicável sem
 * separador e sem hover — sem erro nenhum no console.
 *
 * Existe para que os hex morem só no `PANEL_STYLES` acima: uma cópia a mais é
 * uma que fica para trás quando o token do painel muda.
 */
export const panelCssVars = (estilo) => {
  const tokens = PANEL_STYLES[estilo] || PANEL_STYLES.flatGray
  return {
    '--panel-bg': tokens.bg,
    '--panel-bg-hover': tokens.bgHover,
    '--panel-scroll-track': tokens.scrollTrack,
  }
}

export const CONVERSA_STATUS = {
  ABERTA: 'aberta',
  AGUARDANDO: 'aguardando',
  EM_ATENDIMENTO: 'em_atendimento',
  ESCALADA: 'escalada',
  FECHADA: 'fechada',
}

export const CONVERSA_STATUS_LABELS = {
  aberta: 'Aberta',
  aguardando: 'Aguardando',
  em_atendimento: 'Em Atendimento',
  escalada: 'Escalada',
  fechada: 'Fechada',
}

export const CONVERSA_STATUS_COLORS = {
  aberta: '#2196F3',
  aguardando: '#FF9800',
  em_atendimento: '#4CAF50',
  escalada: '#F44336',
  fechada: '#9E9E9E',
}

/**
 * Tokens de cor específicos do chat/atendimento.
 *
 * Por que existem? São cores semanticamente específicas de um cliente de
 * mensageria (bolha WhatsApp, nota interna amber, link azul), que não têm
 * equivalente direto no design-system global (`_vars.sass`/`:root` CSS vars).
 * Extraídas aqui para evitar hex scattered e centralizar o significado de
 * cada cor. Cores com equivalente próximo no design-system (ex.: `#FFFFFF`,
 * `#e0e0e0`) NÃO estão aqui — usam `var(--white)`, `var(--gray-3)` etc.
 *
 * Consumo: via `:style` binding nos componentes (Vue template/script).
 * Para uso em `<style scoped>` (CSS), os mesmos valores são espelhados como
 * SASS `$chat-*` no topo do bloco `<style>` do `chat-message.vue`.
 */
export const CHAT_COLORS = {
  /** Bolha de mensagem recebida (incoming) — fundo branco limpo. */
  BUBBLE_INCOMING_BG: '#FFFFFF',
  /** Bolha de mensagem enviada (outgoing) — mint WhatsApp clássico. */
  BUBBLE_OUTGOING_BG: '#dcf8c6',
  /** Bolha de nota interna — creme pastel para diferenciar de mensagens reais. */
  BUBBLE_NOTE_BG: '#FFF8E1',
  /** Borda amarela clara da bolha de nota interna. */
  NOTE_BORDER: '#FFE082',
  /** Cor do nome do profissional em notas internas (azul material). */
  NOTE_PROFESSIONAL_COLOR: '#1976D2',
  /** Cor do nome do remetente em mensagens de grupo (roxo para contraste). */
  SENDER_NAME_COLOR: '#6B4FAF',
  /** Cor de link clicável em mensagens (material blue — convenção de chat). */
  LINK_COLOR: '#1976D2',
  /** Cor de link em hover (material blue escuro). */
  LINK_HOVER_COLOR: '#1565C0',
  /** Cor do texto do contador de reações (gray mid-tone). */
  REACTION_COUNT_COLOR: '#666666',
  /** Borda do textarea de edição inline de nota (amber médio). */
  INLINE_EDIT_BORDER: '#FFB300',
  /** Borda/bg do botão primário de edição inline e focus do textarea (amber escuro). */
  INLINE_EDIT_PRIMARY: '#F57F17',
  /** Hover do botão primário de edição inline (amber mais escuro). */
  INLINE_EDIT_PRIMARY_HOVER: '#E65100',
}

export const CONVERSA_STATUS_OPTIONS = [
  { text: 'Todas', value: null },
  { text: 'Abertas', value: 'aberta' },
  { text: 'Aguardando', value: 'aguardando' },
  { text: 'Em Atendimento', value: 'em_atendimento' },
  { text: 'Escaladas', value: 'escalada' },
  { text: 'Fechadas', value: 'fechada' },
]

// Copy (PT-BR) e mensagens da seção de participantes de grupo no painel
// lateral (client-detail-panel). Centralizado aqui para evitar strings
// soltas no template/script.
export const PARTICIPANTS_TEXT = {
  SECTION_TITLE: 'Participantes',
  REFRESH: 'Atualizar participantes',
  REFRESH_ERROR: 'Não foi possível atualizar os participantes.',
  ADMIN_BADGE: 'Admin',
  NO_NUMBER: 'Sem número',
  START_CONVERSATION: 'Iniciar conversa',
  ADD_CONTACT: 'Adicionar aos contatos',
  ADDED: 'Adicionado',
  LID_DISABLED_TOOLTIP: 'Número não disponível',
  ADD_CONTACT_ERROR: 'Não foi possível adicionar o contato.',
  CONNECTION_MISSING:
    'Não foi possível identificar a conexão do grupo. Recarregue a conversa e tente novamente.',
  EMPTY: 'Nenhum participante encontrado.',
}

// Copy (PT-BR) das ações de cabeçalho do contato no painel lateral
// (client-detail-panel): recarregar dados (nome/foto) da Evolution e editar
// o nome do contato (override manual `nome_contato`). Centralizado aqui para
// evitar strings soltas no template/script.
export const CONTACT_TEXT = {
  REFRESH_DATA: 'Recarregar dados',
  REFRESH_DATA_ERROR: 'Não foi possível recarregar os dados do contato.',
  EDIT_NAME: 'Editar nome',
  EDIT_NAME_PLACEHOLDER: 'Nome do contato',
  EDIT_NAME_SAVE: 'Salvar',
  EDIT_NAME_CANCEL: 'Cancelar',
  EDIT_NAME_ERROR: 'Não foi possível atualizar o nome do contato.',
}

export const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏']

export const MEDIA_TYPES = ['imagem', 'audio', 'video', 'documento', 'sticker']

export const DEFAULT_TIPO_ICON = {
  icon: 'check-circle',
  color: '#8E8E8E',
  iconSize: 14,
  name_pretty: '',
}


// Strings e tokens do fluxo "Criar tarefa a partir de mensagem" (M1).
// EXTERNAL_SOURCE é o valor fixo do vínculo (pm_task.external_source) e
// TITLE_MAX_LEN o limite de truncamento do título derivado da mensagem.
export const TASK_FROM_MESSAGE = {
  EXTERNAL_SOURCE: 'atendimento_mensagem',
  MENU_LABEL: 'Criar tarefa',
  MENU_ICON: 'mdi-clipboard-check-outline',
  BADGE_LABEL: 'Tarefa',
  BADGE_ICON: 'mdi-clipboard-check-outline',
  BADGE_TOOLTIP: 'Abrir tarefa vinculada',
  TITLE_MAX_LEN: 80,
  DESCRIPTION_ATTRIBUTION_IN: 'Mensagem de {nome}:',
  DESCRIPTION_ATTRIBUTION_OUT: 'Mensagem enviada por você:',
}

// Strings e tokens do fluxo "Enviar para AAWZ Drive" a partir de uma mensagem
// com mídia. Os dois itens abrem um menu em cascata com a árvore de pastas do
// Drive do cliente; MENU_LABEL_RENAME ainda passa pelo modal de renomear antes
// de enviar.
export const SEND_TO_DRIVE = {
  MENU_LABEL: 'Enviar para AAWZ Drive',
  MENU_LABEL_RENAME: 'Enviar para AAWZ Drive e renomear',
  MENU_ICON: 'folder-cloud',
}

// Strings da seção "Notas internas" da sidebar (M2).
export const NOTES_SECTION = {
  TITLE: 'Notas internas',
  ICON: 'mdi-note-text-outline',
  EMPTY: 'Nenhuma nota interna nesta conversa',
  LOADING_FIND: 'Carregando mensagens...',
  NOT_FOUND: 'Não foi possível localizar a nota no histórico.',
}

// Rótulo de autor de nota quando o nome do profissional não está disponível.
export const NOTA_AUTOR_FALLBACK = 'Nota interna'

// Copy (PT-BR) do scroll-histórico progressivo. Quando o banco esgota, o
// scroll-até-o-topo dispara a importação do WhatsApp (Evolution); estes
// rótulos alimentam o spinner e o indicador de fim de histórico.
export const HISTORY_SCROLL = {
  // Spinner enquanto pagina o banco (rápido).
  LOADING_DB: 'Carregando mensagens...',
  // Spinner enquanto importa o lote anterior do WhatsApp (pode demorar).
  LOADING_WHATSAPP: 'Buscando mensagens anteriores no WhatsApp...',
  // Fim absoluto: banco e WhatsApp esgotados.
  BEGINNING: 'Início da conversa',
  // Falha ao importar do WhatsApp (timeout do Evolution é comum) — retry.
  IMPORT_ERROR:
    'Não foi possível buscar mensagens anteriores agora. Tente rolar novamente em instantes.',
}

// Tamanho do lote pedido ao importar mensagens anteriores do WhatsApp por
// gesto de scroll-até-o-topo. Mantido conservador para não sobrecarregar o
// Evolution nem segurar o request por muito tempo.
export const IMPORT_ANTERIORES_BATCH = 100

// Strings da seção "Tarefas (Gerenciador)" da sidebar (M1 — reverse link).
export const TAREFAS_VINCULADAS_SECTION = {
  TITLE: 'Tarefas (Gerenciador)',
  EMPTY: 'Nenhuma tarefa vinculada',
  TITLE_FALLBACK: 'Tarefa sem título',
  DUE_PREFIX: 'Vence',
}

// Ação do cabeçalho da seção "Tarefas" da sidebar: cria tarefa avulsa, sem
// vínculo com mensagem, pré-preenchida com o contexto da conversa aberta.
export const TASKS_SECTION_ACTION = {
  ICON: 'add-circle',
  ICON_SIZE: '14',
  TOOLTIP: 'Nova tarefa',
}

export const ICONS = {
  // Ações de conexão (Settings + Chat)
  qrcode: 'mdi-qrcode',
  refresh: 'refresh',
  disconnect: 'mdi-link-off',
  deactivate: 'mdi-power-plug-off',
  assignProfissionais: 'mdi-account-group',

  // Ações de mensagem
  send: 'mdi-send',
  reply: 'mdi-reply',
  edit: 'edit-2',
  react: 'mdi-emoticon-happy-outline',
  delete: 'trash',
  close: 'close',
  attachment: 'mdi-paperclip',
  quickReplies: 'mdi-lightning-bolt',
  quickRepliesEmpty: 'mdi-lightning-bolt-outline',
  noteEdit: 'mdi-note-edit-outline',
  noteAdd: 'mdi-note-plus-outline',
  noteIndicator: 'mdi-note-text-outline',
  lock: 'mdi-lock',
  lockOutline: 'mdi-lock-outline',

  // Status de entrega
  statusSending: 'mdi-clock-outline',
  statusSent: 'mdi-check',
  statusDelivered: 'mdi-check-all',
  statusError: 'mdi-alert-circle',
  checkCircle: 'mdi-check-circle',
  alertOutline: 'mdi-alert-outline',

  // Navegação / UI
  plus: 'mdi-plus',
  dotsVertical: 'mdi-dots-vertical',
  settings: 'mdi-cog',
  history: 'mdi-history',
  calendarRange: 'mdi-calendar-range',
  chatOutline: 'message-text',
  messageTextOutline: 'message',
  connections: 'mdi-link',
  whatsapp: 'mdi-whatsapp',
  linkVariant: 'mdi-link-variant',
  linkVariantOff: 'mdi-link-variant-off',
  openInNew: 'mdi-open-in-new',
  download: 'download',

  // Contato / Grupo
  account: 'mdi-account',
  accountPlus: 'mdi-account-plus-outline',
  accountCheck: 'mdi-account-check-outline',
  accountGroup: 'mdi-account-group',
  accountGroupOutline: 'mdi-account-group-outline',
  accountQuestion: 'mdi-account-question-outline',
  accountSearch: 'mdi-account-search-outline',
  accountDetails: 'user-2',

  // Mensagens de sistema
  transfer: 'mdi-arrow-right-bold',
  info: 'mdi-information-outline',

  // Mídia
  musicNote: 'mdi-music-note',
  play: 'mdi-play',
  pause: 'mdi-pause',

  // Mídia — error/document/file (usados em computeds)
  imageOff: 'mdi-image-off-outline',
  musicNoteOff: 'mdi-music-note-off',
  videoOff: 'mdi-video-off-outline',
  fileRemove: 'mdi-file-remove-outline',
  fileDocument: 'mdi-file-document-outline',
  filePdf: 'mdi-file-pdf-box',
  fileWord: 'mdi-file-word-box',
  fileExcel: 'mdi-file-excel-box',
}

export const SHOW_ATENDIMENTO_BLACKLIST = [
  'crm.atendimento',
  'solicitacoes.comunicacao',
  // Visão imersiva do chat de IA: a tela não tem plataforma em volta, e um FAB
  // flutuando sobre ela seria o único chrome sobrevivente.
  'ia-imersivo',
]

// Chave do deep link `?conversa=<id>` da caixa (`crm.atendimento`) — a mesma
// nos dois lados: quem abre (`ia/acoes.js`) e quem lê (`utils/conversa-da-rota`).
export const QUERY_CONVERSA = 'conversa'

// Botão "Resumir não lidas com a IA": o prompt vive no backend (chave
// `resumo_nao_lidas` em `apiPerguntasPredefinidas`); aqui só o que a tela diz.
export const RESUMO_NAO_LIDAS_ERRO =
  'Não foi possível preparar o resumo agora. Tente novamente em instantes.'
